import { useRef, useState } from "react";
import { api, puede, ErrorApi } from "../api.ts";
import { Camara, camaraDisponible } from "./Camara.tsx";

/** Recorta al centro en cuadrado (para el avatar) o reduce manteniendo la proporción. */
async function procesar(archivo: File): Promise<{ imagen: string; miniatura: string }> {
  const bmp = await createImageBitmap(archivo, { imageOrientation: "from-image" } as ImageBitmapOptions).catch(() => { throw new ErrorApi(400, "No se pudo leer la imagen."); });
  const c = document.createElement("canvas"), x = c.getContext("2d")!;
  const k = Math.min(1, 1200 / Math.max(bmp.width, bmp.height));
  c.width = Math.round(bmp.width * k); c.height = Math.round(bmp.height * k);
  x.drawImage(bmp, 0, 0, c.width, c.height);
  const imagen = c.toDataURL("image/jpeg", 0.88);
  const lado = Math.min(bmp.width, bmp.height);
  c.width = c.height = 256;
  x.drawImage(bmp, (bmp.width - lado) / 2, (bmp.height - lado) / 2, lado, lado, 0, 0, 256, 256);
  return { imagen, miniatura: c.toDataURL("image/jpeg", 0.82) };
}

const iniciales = (nombre: string) => nombre.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? "").join("");

/** Foto de la cara junto al nombre del paciente. Al tocarla se toma con la cámara o se elige un archivo. */
export function FotoPerfil({ pacienteId, nombre, foto, alCambiar }: { pacienteId: string; nombre: string; foto: string | null; alCambiar: (f: string) => void }) {
  const [menu, setMenu] = useState(false);
  const [camara, setCamara] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState("");
  const archivo = useRef<HTMLInputElement>(null), nativa = useRef<HTMLInputElement>(null);
  const puedeCambiar = puede("pacientes:escribir");

  async function guardar(f: File) {
    setOcupado(true); setError(""); setMenu(false); setCamara(false);
    try {
      const r = await api<{ foto: string }>("PUT", `/api/pacientes/${pacienteId}/foto`, await procesar(f));
      alCambiar(r.foto);
    } catch (e) { setError(e instanceof ErrorApi ? e.message : "No se pudo guardar la foto."); }
    finally { setOcupado(false); }
  }

  return (
    <div className="avatar-caja">
      <button className={`avatar${foto ? "" : " vacio"}`} disabled={!puedeCambiar || ocupado} onClick={() => setMenu(!menu)}
        aria-label={foto ? "Cambiar la foto del paciente" : "Tomar la foto del paciente"} title={puedeCambiar ? "Foto del paciente" : undefined}>
        {foto ? <img src={foto} alt={`Foto de ${nombre}`} /> : <span>{iniciales(nombre)}</span>}
        {puedeCambiar && <i aria-hidden="true">📷</i>}
      </button>
      {menu && (
        <div className="avatar-menu" role="menu">
          <button role="menuitem" onClick={() => { setMenu(false); if (camaraDisponible()) setCamara(true); else nativa.current?.click(); }}>Tomar foto</button>
          <button role="menuitem" onClick={() => { setMenu(false); archivo.current?.click(); }}>Elegir archivo</button>
        </div>
      )}
      <input ref={nativa} type="file" accept="image/*" capture="user" hidden onChange={(e) => e.target.files?.[0] && guardar(e.target.files[0])} />
      <input ref={archivo} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => e.target.files?.[0] && guardar(e.target.files[0])} aria-label="Archivo de la foto del paciente" />
      {camara && <Camara frontal alCapturar={guardar} alCerrar={() => setCamara(false)} />}
      {error && <span className="aviso error avatar-error">{error}</span>}
    </div>
  );
}
